// Attendance API service

import { apiRequest } from './api';
import type { AttendanceStatus, StaffStatus } from '../components/attendance/attendanceMockData';

// ── Types ───────────────────────────────────────────────────

export type SaveStudentAttendancePayload = {
    date: string;
    academic_session_id: string;
    class_id: string;
    section_id: string | null;
    school_id:string | null;
    records: {
        student_id: string;
        status: AttendanceStatus;
        remarks: string | null;
    }[];
};

export type StudentAttendanceRecord = {
    id: string;
    organization_id: string;
    academic_session_id: string;
    student_id: string;
    class_id: string;
    section_id: string | null;
    date: string;
    status: AttendanceStatus;
    remarks: string | null;
    recorded_by_user_id: string;
    created_at: string;
    updated_at: string;
    student: {
        id: string;
        enrollment_number: string | null;
        roll_number: string | null;
        user: {
            id: string;
            first_name: string;
            last_name: string | null;
        };
    };
};

// ── API Functions ───────────────────────────────────────────

export async function fetchStudentAttendance(params: {
    date?: string;
    class_id: string;
    section_id?: string;
    per_page?: number;
}): Promise<StudentAttendanceRecord[]> {
    const query = new URLSearchParams();
    if (params.date) query.set('date', params.date);
    query.set('class_id', params.class_id);
    if (params.section_id) query.set('section_id', params.section_id);
    query.set('per_page', String(params.per_page ?? 100));

    const res = await apiRequest<StudentAttendanceRecord[]>(
        `/api/attendance/students?${query.toString()}`
    );
    return res.data;
}

export async function saveStudentAttendance(
    payload: SaveStudentAttendancePayload
): Promise<void> {
    await apiRequest('/api/attendance/students', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// ── Staff Attendance ────────────────────────────────────────

export type StaffAttendanceApiRecord = {
    id: string;
    organization_id: string;
    staff_id: string;
    date: string;
    status: StaffStatus;
    clock_in_time: string | null;
    clock_out_time: string | null;
    is_late: boolean;
    late_minutes: number;
    ip_address: string | null;
    device_id: string | null;
    remarks: string | null;
    created_at: string;
    updated_at: string;
    staff: {
        id: string;
        employee_id: string;
        user: {
            id: string;
            first_name: string;
            last_name: string | null;
        };
    };
};

export async function fetchStaffAttendance(params: {
    date: string;
    staff_id?: string;
    status?: string;
    per_page?: number;
}): Promise<StaffAttendanceApiRecord[]> {
    const query = new URLSearchParams();
    query.set('date', params.date);
    if (params.staff_id) query.set('staff_id', params.staff_id);
    if (params.status) query.set('status', params.status);
    query.set('per_page', String(params.per_page ?? 50));

    const res = await apiRequest<StaffAttendanceApiRecord[]>(
        `/api/attendance/staff?${query.toString()}`
    );
    return res.data;
}

export type SaveStaffAttendanceRecord = {
    staff_id: string;
    status: StaffStatus;
    clock_in_time: string | null;
    clock_out_time: string | null;
    is_late?: boolean;
    late_minutes?: number;
    ip_address?: string | null;
    device_id?: string | null;
    remarks?: string | null;
};

export type SaveStaffAttendancePayload = {
    date: string;
    school_id:string | null;
    records: SaveStaffAttendanceRecord[];
};

export async function saveStaffAttendance(
    payload: SaveStaffAttendancePayload
): Promise<void> {
    await apiRequest('/api/attendance/staff', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// ── Monthly Attendance Report ────────────────────────────────

export type MonthlyAttendanceRecord = {
    id: string;
    enrollment_number: string;
    roll_number: string;
    student: { first_name: string; last_name: string | null };
    class: { id: string; name: string };
    section: { id: string; name: string } | null;
    days: Record<string, string | null>;
    summary: unknown[];
};

export type MonthlyAttendanceMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    month: string;
    days_in_month: number;
};

export type MonthlyAttendanceResponse = {
    data: MonthlyAttendanceRecord[];
    meta: MonthlyAttendanceMeta;
};

export async function fetchMonthlyAttendanceReport(params: {
    month: string;
    class_id: string;
}): Promise<MonthlyAttendanceResponse> {
    const query = new URLSearchParams();
    query.set('month', params.month);
    query.set('class_id', params.class_id);
    const res = await apiRequest<MonthlyAttendanceRecord[]>(
        `/api/reports/monthly-attendance?${query.toString()}`
    );
    return {
        data: res.data,
        meta: (res as unknown as { meta: MonthlyAttendanceMeta }).meta,
    };
}
